import { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';

interface AudioOperationStatus {
  id: string;
  status: string;
  total_items: number | null;
  processed_items: number | null;
  successful_items: number | null;
  failed_items: number | null;
  started_at: string | null;
  completed_at: string | null;
  last_heartbeat: string | null;
}

export const GenerationStatusMonitor = () => {
  const [audioOperation, setAudioOperation] = useState<AudioOperationStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorBreakdown, setErrorBreakdown] = useState<Record<string, number>>({});
  const [hasAuthErrors, setHasAuthErrors] = useState(false);
  const [totalModulesNeedingAudio, setTotalModulesNeedingAudio] = useState(0);

  const handleResetOperation = async () => {
    if (!audioOperation) return;
    
    try {
      setLoading(true);
      
      // Cancel the stuck operation
      await supabase
        .from('audio_generation_operations')
        .update({ 
          status: 'cancelled',
          error_message: 'Reset by admin - operation was stuck with incorrect total',
          completed_at: new Date().toISOString()
        })
        .eq('id', audioOperation.id);

      toast.success('Cancelling stuck operation...');

      // Wait a moment, then trigger auto-resume
      setTimeout(async () => {
        const { error } = await supabase.functions.invoke('auto-resume-audio');
        
        if (error) {
          toast.error(`Failed to restart: ${error.message}`);
        } else {
          toast.success('New operation starting with correct module count!');
        }
        
        setLoading(false);
      }, 2000);
      
    } catch (error: any) {
      console.error('Error resetting operation:', error);
      toast.error(`Failed to reset: ${error.message}`);
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadStatus = async () => {
      try {
        // Get the most recent audio generation operation
        const { data } = await supabase
          .from('audio_generation_operations')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        setAudioOperation(data);

        // Count modules needing audio
        const { count } = await supabase
          .from('course_modules')
          .select('*', { count: 'exact', head: true })
          .eq('is_enabled', true);

        // Get modules with audio to calculate missing
        const { data: audioModules } = await supabase
          .from('module_audio_content')
          .select('module_id')
          .not('audio_url', 'is', null);

        const totalActive = count || 0;
        const withAudio = audioModules?.length || 0;
        setTotalModulesNeedingAudio(totalActive - withAudio);
      } catch (error) {
        console.error('Error loading audio operation status:', error);
      } finally {
        setLoading(false);
      }
    };

    loadStatus();

    // Subscribe to real-time updates
    const channel = supabase
      .channel('audio-operation-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'audio_generation_operations'
        },
        () => loadStatus()
      )
      .subscribe();

    // Refresh every 10 seconds
    const interval = setInterval(loadStatus, 10000);

    return () => {
      supabase.removeChannel(channel);
      clearInterval(interval);
    };
  }, []);

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span className="ml-2">Loading generation status...</span>
        </div>
      </Card>
    );
  }

  if (!audioOperation) {
    return (
      <Card className="p-6">
        <h2 className="text-2xl font-bold mb-4">Audio Generation Status</h2>
        <p className="text-muted-foreground">No active or recent audio generation operations found.</p>
      </Card>
    );
  }

  const progress = audioOperation.total_items 
    ? ((audioOperation.processed_items || 0) / audioOperation.total_items) * 100 
    : 0;

  const isActive = audioOperation.status === 'in_progress';
  const isCompleted = audioOperation.status === 'completed';

  const isStuck = audioOperation.processed_items && audioOperation.total_items && 
                  audioOperation.processed_items > audioOperation.total_items;

  return (
    <Card className="p-6">
      <h2 className="text-2xl font-bold mb-4">Audio Generation Status</h2>
      
      <div className="space-y-4">
        {/* OpenAI API Key Error - CRITICAL */}
        {hasAuthErrors && isActive && (
          <Alert variant="destructive" className="border-2">
            <AlertCircle className="h-5 w-5" />
            <AlertDescription>
              <div className="space-y-3">
                <p className="font-bold text-lg">⚠️ CRITICAL: OpenAI API Key Error</p>
                <p className="text-sm">
                  {errorBreakdown.auth_error || 0} modules are failing with <strong>403 Forbidden errors</strong>. 
                  This means your OpenAI API key is invalid, expired, or out of credits.
                </p>
                <div className="bg-background/50 p-3 rounded space-y-2 text-sm">
                  <p className="font-semibold">🔧 Fix Required (2 minutes):</p>
                  <ol className="list-decimal list-inside space-y-1 ml-2">
                    <li>Go to <strong>Admin → Configuration → Secrets</strong></li>
                    <li>Check if <code className="bg-muted px-1 py-0.5 rounded">OPENAI_API_KEY</code> is valid</li>
                    <li>Get a new key from <a href="https://platform.openai.com/api-keys" target="_blank" className="underline text-primary">platform.openai.com/api-keys</a></li>
                    <li>Verify billing is active and credits available</li>
                  </ol>
                </div>
                <p className="text-sm font-semibold">
                  ⚠️ DO NOT click "Reset & Restart" until the API key is fixed - it will just fail again!
                </p>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Stuck operation warning */}
        {isStuck && isActive && !hasAuthErrors && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="flex items-center justify-between gap-4">
              <div className="flex-1">
                <p className="font-semibold">Operation Stuck</p>
                <p className="text-sm mt-1">
                  Processed {audioOperation.processed_items} items but total was only {audioOperation.total_items}. 
                  There are <strong>{totalModulesNeedingAudio} modules</strong> still needing audio.
                </p>
              </div>
              <Button 
                size="sm" 
                variant="outline"
                onClick={handleResetOperation}
                disabled={loading}
                className="shrink-0"
              >
                Reset & Restart
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Ready to reset after API key fix */}
        {hasAuthErrors && !isActive && totalModulesNeedingAudio > 0 && (
          <Alert className="border-green-500 bg-green-500/10">
            <AlertCircle className="h-4 w-4 text-green-600" />
            <AlertDescription className="flex items-center justify-between gap-4">
              <div className="flex-1">
                <p className="font-semibold text-green-700">Ready to Resume</p>
                <p className="text-sm mt-1">
                  After fixing the API key, click here to process the remaining <strong>{totalModulesNeedingAudio} modules</strong>.
                </p>
              </div>
              <Button 
                size="sm" 
                onClick={handleResetOperation}
                disabled={loading}
                className="shrink-0 bg-green-600 hover:bg-green-700"
              >
                Start New Operation
              </Button>
            </AlertDescription>
          </Alert>
        )}

        <div>
          <div className="flex justify-between mb-2">
            <span className="font-medium">AI-Powered TTS Audio</span>
            <span className="font-mono">
              {audioOperation.processed_items || 0} / {audioOperation.total_items || 0}
            </span>
          </div>
          <Progress value={progress} className="h-3" />
          <div className="flex justify-between text-sm text-muted-foreground mt-1">
            <span>{progress.toFixed(1)}% complete</span>
            {isActive && <span className="text-primary animate-pulse">● Processing...</span>}
            {isCompleted && <span className="text-green-600">✓ Complete</span>}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mt-6">
          <div className="p-4 bg-green-50 dark:bg-green-950 rounded-lg border border-green-200 dark:border-green-800">
            <p className="text-sm text-muted-foreground">Successful</p>
            <p className="text-2xl font-bold text-green-600">{audioOperation.successful_items || 0}</p>
          </div>
          <div className="p-4 bg-red-50 dark:bg-red-950 rounded-lg border border-red-200 dark:border-red-800">
            <p className="text-sm text-muted-foreground">Failed</p>
            <p className="text-2xl font-bold text-red-600">{audioOperation.failed_items || 0}</p>
          </div>
        </div>

        {/* Error Breakdown */}
        {Object.keys(errorBreakdown).length > 0 && (
          <div className="mt-4 p-4 bg-destructive/10 rounded-lg border border-destructive/20">
            <h3 className="font-semibold mb-2 text-sm">Error Categories</h3>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(errorBreakdown).map(([category, count]) => (
                <div key={category} className="flex justify-between text-xs">
                  <span className="text-muted-foreground capitalize">{category.replace('_', ' ')}:</span>
                  <span className="font-medium">{count}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-4 p-4 bg-secondary rounded-lg space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Status:</span>
            <span className="font-medium capitalize">{audioOperation.status.replace('_', ' ')}</span>
          </div>
          {audioOperation.started_at && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Started:</span>
              <span className="font-medium">{new Date(audioOperation.started_at).toLocaleString()}</span>
            </div>
          )}
          {audioOperation.completed_at && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Completed:</span>
              <span className="font-medium">{new Date(audioOperation.completed_at).toLocaleString()}</span>
            </div>
          )}
          {audioOperation.last_heartbeat && isActive && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Last Activity:</span>
              <span className="font-medium">{new Date(audioOperation.last_heartbeat).toLocaleString()}</span>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};
